/**
 * ClippedImage Component
 *
 * A React component that displays an image with animated clip paths.
 * The clip paths can be expanded/collapsed based on the isExpanded prop.
 *
 * @param {Object} props
 * @param {string} props.src - Source URL of the image
 * @param {string} props.alt - Alt text for the image
 * @param {Array<Object>} props.clipPaths - Array of clip path configurations
 * @param {Object} props.clipPaths[].initial - Initial state of clip path
 * @param {Object} props.clipPaths[].expanded - Expanded state of clip path
 * @param {number} [props.transitionDuration=0.8] - Duration of expansion animation in seconds
 * @param {number} [props.transitionDelay=0] - Delay before animation starts in seconds
 * @param {string} [props.transitionEase='easeOut'] - Easing function for animation
 * @param {boolean} [props.isExpanded=false] - Whether clip paths should be expanded
 * @param {Object} [props.dimensions] - Provided dimensions for more accurate calculations
 * @param {Function} [props.onLoad] - Callback fired when image loads
 */

import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';
import Reticle from './Reticle';
import { generateUniqueId } from '../utils/clipPath';

//todo: Masthead clip path doesn't work in Safari
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

// Styled Components
const Container = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
	visibility: ${(props) => (props.$isLoaded ? 'visible' : 'hidden')};
	-webkit-transform: translateZ(0); /* Safari GPU acceleration */
	-moz-transform: translateZ(0);
	transform: translateZ(0);

	img {
		width: 100%;
		height: auto;
		display: block;
		object-fit: ${(props) => (props.$isExpanded ? 'cover' : 'contain')};
		margin: 0;
		-webkit-transform: translate3d(0, 0, 0);
		transform: translate3d(0, 0, 0);
		-webkit-backface-visibility: hidden; /* Helps with Safari rendering */
		backface-visibility: hidden;
		-webkit-perspective: 1000;
		perspective: 1000;
		will-change: transform, clip-path;

		@media (min-width: 768px) {
			height: 100%;
		}
	}

	.clip-svg {
		-webkit-clip-path: url(#${(props) => props.$clipId});
		clip-path: url(#${(props) => props.$clipId});
	}
`;

export const ClippedImage = ({ src, alt, clipPaths = [], transitionDuration = 0.8, transitionDelay = 0, transitionEase = 'easeOut', isExpanded = false, dimensions, onLoad }) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [clipId] = useState(generateUniqueId());
	const [reticleActive, setReticleActive] = useState(true);
	const [calculatedDimensions, setCalculatedDimensions] = useState({
		width: 0,
		height: 0,
		left: 0,
		top: 0,
	});
	const [shouldExpand, setShouldExpand] = useState(false);
	const imageRef = useRef(null);

	// Handle expansion state changes
	useEffect(() => {
		const timer = setTimeout(() => {
			setShouldExpand(isExpanded);
		}, 800);

		return () => clearTimeout(timer);
	}, [isExpanded]);

	// Calculate and update image dimensions
	useEffect(() => {
		const img = new Image();
		img.src = src;
		img.onload = () => {
			setIsLoaded(true);
			updateDimensions();
			if (onLoad) onLoad();
		};

		const updateDimensions = () => {
			if (imageRef.current) {
				const container = imageRef.current.getBoundingClientRect();
				const isMobile = window.innerWidth < 768;

				// Use provided dimensions if available, otherwise use natural image dimensions
				const imgRatio = dimensions ? dimensions.width / dimensions.height : 1.5;

				let width, height;
				if (isMobile) {
					// On mobile, use the container width and maintain aspect ratio
					width = container.width;
					height = width / imgRatio;
				} else {
					// On desktop, use the container dimensions
					const containerRatio = container.width / container.height;
					if (containerRatio > imgRatio) {
						// Container is wider than image ratio - constrain by height
						height = container.height;
						width = height * imgRatio;
					} else {
						// Container is taller than image ratio - constrain by width
						width = container.width;
						height = width / imgRatio;
					}
				}

				// Center image within container
				const left = (container.width - width) / 2;
				const top = isMobile ? 0 : (container.height - height) / 2;

				setCalculatedDimensions({
					width,
					height,
					left,
					top,
				});
			}
		};

		updateDimensions();
		window.addEventListener('resize', updateDimensions);
		return () => window.removeEventListener('resize', updateDimensions);
	}, [dimensions, onLoad, src]);

	// Calculate final values for clip path, accounting for image position
	const getPathValues = (path) => {
		// Convert percentage values to pixels
		const calculateValue = (value, baseDimension) => (typeof value === 'string' && value.endsWith('%') ? (parseFloat(value) / 100) * baseDimension : value);

		return {
			x: calculateValue(path.x, calculatedDimensions.width) + calculatedDimensions.left,
			y: calculateValue(path.y, calculatedDimensions.height) + calculatedDimensions.top,
			width: path.width ? calculateValue(path.width, calculatedDimensions.width) : calculateValue(path.height, calculatedDimensions.height),
			height: path.height ? calculateValue(path.height, calculatedDimensions.height) : calculateValue(path.width, calculatedDimensions.width),
		};
	};

	// Handle key down and key up events for reticle activation
	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === 'd') {
				setReticleActive(true);
			}
		};

		const handleKeyUp = (e) => {
			if (e.key === 'd') {
				setReticleActive(false);
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUp);
		};
	}, []);

	const handleImageLoad = (e) => {
		// Initialize dimensions on image load
		const rect = imageRef.current?.getBoundingClientRect();
		if (rect) {
			setCalculatedDimensions({
				width: rect.width,
				height: rect.height,
				left: 0,
				top: 0,
			});
		}
		setIsLoaded(true);
		onLoad?.(e);
	};

	return (
		<Container ref={imageRef} $clipId={clipId} $isExpanded={isExpanded} $isLoaded={isLoaded} className="clipped-image-container">
			<img src={src} alt={alt} className={`clipped-image ${!isSafari ? 'clip-svg' : ''}`} onLoad={handleImageLoad} />
			<Reticle active={reticleActive} />
			{/* Clip Path SVG */}
			<svg
				style={{
					position: 'absolute',
					inset: 0,
					width: '100%',
					height: '100%',
					pointerEvents: 'none',
				}}
				preserveAspectRatio="none"
				className="svg-container"
			>
				<defs>
					<clipPath id={clipId} className="clip-path">
						{calculatedDimensions.width > 0 &&
							calculatedDimensions.height > 0 &&
							clipPaths.map((path, index) => {
								const initial = getPathValues(path.initial);
								const expanded = getPathValues(path.expanded);
								return (
									<motion.rect
										key={index}
										style={{ position: 'absolute' }}
										initial={initial}
										animate={{
											x: shouldExpand ? expanded.x : initial.x,
											y: shouldExpand ? expanded.y : initial.y,
											width: shouldExpand ? expanded.width : initial.width,
											height: shouldExpand ? expanded.height : initial.height,
										}}
										transition={{
											duration: transitionDuration,
											delay: transitionDelay,
											ease: transitionEase,
										}}
										className={`clip-rect clip-rect-${index}`}
									/>
								);
							})}
					</clipPath>
				</defs>
			</svg>
		</Container>
	);
};
